import { useMutation, useQuery } from "@apollo/client/react"
import { X } from "lucide-react"
import React, { useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { UserSearchSettingsQuery } from "~/__generated__/graphql"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/graphql-error"
import { ScreenLoading } from "~/ui/screen-loading"
import { Sheet, SheetContent, SheetTitle } from "~/ui/sheet"
import { buildColumns } from "./columns"
import { DataTable } from "./data-table"

export const usersQuery = gql(/* GraphQL */ `
  query Users($first: Int = 20, $after: String) {
    internalAdminUsers(first: $first, after: $after) {
      edges {
        node {
          id
          fullName
          email
          createdAt
          searchKeywords {
            id
            keyword
          }
          waitlistEntry {
            keywords
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

const USER_SEARCH_SETTINGS_QUERY = gql(/* GraphQL */ `
  query UserSearchSettings($id: ID!) {
    node(id: $id) {
      ... on User {
        id
        fullName
        email
        searchKeywords {
          id
          keyword
          active
          position
        }
        userLocations {
          id
          position
          cityZip
          radius
        }
      }
    }
  }
`)

const ADMIN_USER_SEARCH_SETTINGS_UPDATE = gql(/* GraphQL */ `
  mutation AdminUserSearchSettingsUpdate($input: InternalAdminUserSearchSettingsUpdateInput!) {
    internalAdminUserSearchSettingsUpdate(input: $input) {
      user {
        id
        searchKeywords {
          id
          keyword
          active
          position
        }
        userLocations {
          id
          position
          cityZip
          radius
        }
      }
    }
  }
`)

const INTERNAL_ADMIN_ADD_KEYWORD = gql(/* GraphQL */ `
  mutation InternalAdminAddKeyword($input: InternalAdminAddKeywordInput!) {
    internalAdminAddKeyword(input: $input) {
      user {
        id
        searchKeywords {
          id
          keyword
        }
        waitlistEntry {
          keywords
        }
      }
    }
  }
`)

const INTERNAL_ADMIN_DELETE_KEYWORD = gql(/* GraphQL */ `
  mutation InternalAdminDeleteKeyword($input: InternalAdminDeleteKeywordInput!) {
    internalAdminDeleteKeyword(input: $input) {
      success
    }
  }
`)

const RADIUS_OPTIONS = [
  { value: "everywhere", label: "Everywhere" },
  { value: "25mi", label: "25 miles" },
  { value: "50mi", label: "50 miles" },
  { value: "100mi", label: "100 miles" },
] as const

type KeywordRow = { keyword: string; active: boolean }
type LocationRow = { cityZip: string; radius: string }

type LoadedUser = Extract<NonNullable<UserSearchSettingsQuery["node"]>, { __typename?: "User" }>

function buildInitialLocations(user: LoadedUser): [LocationRow, LocationRow] {
  const locs: [LocationRow, LocationRow] = [
    { cityZip: "", radius: "everywhere" },
    { cityZip: "", radius: "everywhere" },
  ]
  for (const loc of user.userLocations) {
    const idx = loc.position - 1
    if (idx === 0 || idx === 1) locs[idx] = { cityZip: loc.cityZip, radius: loc.radius }
  }
  return locs
}

const UserSearchSettingsForm: React.FC<{
  userId: string
  user: LoadedUser
}> = ({ userId, user }) => {
  const [updateSettings, { loading: saving }] = useMutation(ADMIN_USER_SEARCH_SETTINGS_UPDATE)
  const [keywords, setKeywords] = useState<KeywordRow[]>(() =>
    user.searchKeywords.map((kw: { keyword: string; active: boolean }) => ({
      keyword: kw.keyword,
      active: kw.active,
    }))
  )
  const [locations, setLocations] = useState<[LocationRow, LocationRow]>(() =>
    buildInitialLocations(user)
  )

  const handleSave = async () => {
    await updateSettings({
      variables: {
        input: {
          userId,
          keywords: keywords
            .filter((kw) => kw.keyword.trim().length > 0)
            .map((kw) => ({ keyword: kw.keyword, active: kw.active })),
          locations: locations
            .map((loc, i) => ({ position: i + 1, cityZip: loc.cityZip, radius: loc.radius }))
            .filter((loc) => loc.cityZip.trim().length > 0),
        },
      },
      onError: (err) => toast.error(err.message),
      onCompleted: () => toast.success("Saved"),
    })
  }

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5 space-y-8">
      <section>
        <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-3">
          Keywords
        </p>
        <div className="space-y-2">
          {keywords.map((kw, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={kw.active}
                onChange={(e) => {
                  const next = [...keywords]
                  next[idx] = { ...next[idx], active: e.target.checked }
                  setKeywords(next)
                }}
                className="shrink-0"
              />
              <input
                type="text"
                value={kw.keyword}
                onChange={(e) => {
                  const next = [...keywords]
                  next[idx] = { ...next[idx], keyword: e.target.value }
                  setKeywords(next)
                }}
                className="flex-1 text-[12px] border border-stone-200 bg-white px-3 py-1.5 outline-none focus:border-stone-400"
              />
              <button
                onClick={() => setKeywords(keywords.filter((_, i) => i !== idx))}
                className="text-stone-300 hover:text-red-500 transition-colors"
                aria-label="Delete keyword"
              >
                <X size={13} />
              </button>
            </div>
          ))}
          <button
            onClick={() => setKeywords([...keywords, { keyword: "", active: true }])}
            className="text-[11px] text-stone-400 hover:text-stone-700 transition-colors mt-1"
          >
            + Add keyword
          </button>
        </div>
      </section>

      <section>
        <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-1">
          Location Filter
        </p>
        <p className="text-[11px] text-stone-400 mb-3">Leave blank for all locations.</p>
        <div className="space-y-2">
          {([0, 1] as const).map((idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="text-[10px] text-stone-400 w-4 shrink-0">{idx + 1}</span>
              <input
                type="text"
                placeholder="City or zip code"
                value={locations[idx].cityZip}
                onChange={(e) => {
                  const next: [LocationRow, LocationRow] = [locations[0], locations[1]]
                  next[idx] = { ...next[idx], cityZip: e.target.value }
                  setLocations(next)
                }}
                className="flex-1 text-[12px] border border-stone-200 bg-white px-3 py-1.5 outline-none focus:border-stone-400 placeholder:text-stone-300"
              />
              <select
                value={locations[idx].radius}
                onChange={(e) => {
                  const next: [LocationRow, LocationRow] = [locations[0], locations[1]]
                  next[idx] = { ...next[idx], radius: e.target.value }
                  setLocations(next)
                }}
                className="text-[12px] border border-stone-200 bg-white px-2 py-1.5 outline-none focus:border-stone-400"
              >
                {RADIUS_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </section>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-stone-900 text-stone-50 px-6 py-2.5 text-[12px] tracking-wide hover:bg-stone-700 transition-colors disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  )
}

const UserSearchSettingsPanel: React.FC<{
  userId: string
  onClose: () => void
}> = ({ userId, onClose }) => {
  const { data, loading, error } = useQuery(USER_SEARCH_SETTINGS_QUERY, {
    variables: { id: userId },
  })

  const user = data?.node?.__typename === "User" ? data.node : null

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between px-5 pt-6 pb-4 border-b border-stone-200">
        <div>
          <p className="text-sm font-bold text-stone-900">{user?.fullName ?? "…"}</p>
          <p className="text-xs text-stone-400 mt-0.5">{user?.email}</p>
        </div>
        <button onClick={onClose} className="text-stone-400 hover:text-stone-700 mt-0.5">
          <X size={16} />
        </button>
      </div>

      {loading && <p className="px-5 py-8 text-sm text-stone-400">Loading…</p>}
      {error && (
        <div className="px-5 py-4">
          <GraphqlError error={error} />
        </div>
      )}
      {user && <UserSearchSettingsForm userId={userId} user={user} />}
    </div>
  )
}

type KeywordEditorUser = {
  id: string
  fullName: string
  searchKeywords: Array<{ id: string; keyword: string }>
  waitlistEntry?: { keywords?: string | null } | null
}

const KeywordEditorDialog: React.FC<{
  user: KeywordEditorUser
  onClose: () => void
}> = ({ user, onClose }) => {
  const [keywords, setKeywords] = useState(user.searchKeywords)
  const [input, setInput] = useState("")

  const [addKeyword, { loading: adding }] = useMutation(INTERNAL_ADMIN_ADD_KEYWORD, {
    refetchQueries: [usersQuery],
    onError: (err) => toast.error(err.message),
  })

  const [deleteKeyword, { loading: deleting }] = useMutation(INTERNAL_ADMIN_DELETE_KEYWORD, {
    refetchQueries: [usersQuery],
    onError: (err) => toast.error(err.message),
  })

  const handleAdd = async (keyword: string) => {
    const trimmed = keyword.trim()
    if (!trimmed) return
    const result = await addKeyword({
      variables: { input: { userId: user.id, keyword: trimmed } },
    })
    if (result.data) {
      setKeywords(result.data.internalAdminAddKeyword.user.searchKeywords)
      setInput("")
    }
  }

  const handleDelete = async (keywordId: string) => {
    const result = await deleteKeyword({
      variables: { input: { keywordId } },
    })
    if (result.data?.internalAdminDeleteKeyword.success) {
      setKeywords((prev) => prev.filter((k) => k.id !== keywordId))
    }
  }

  const handleSave = async () => {
    if (input.trim()) {
      await handleAdd(input)
    }
    onClose()
  }

  const busy = adding || deleting

  return (
    <div className="space-y-5">
      {user.waitlistEntry?.keywords && (
        <div>
          <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-1">
            Signed up hunting for
          </p>
          <p className="text-xs text-stone-500">{user.waitlistEntry.keywords}</p>
        </div>
      )}

      <div>
        <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-2">
          Keywords
        </p>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {keywords.length === 0 && <p className="text-xs text-stone-400">No keywords yet.</p>}
          {keywords.map((kw) => (
            <span
              key={kw.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-stone-100 text-stone-700 text-xs rounded-full"
            >
              {kw.keyword}
              <button
                onClick={() => handleDelete(kw.id)}
                disabled={busy}
                className="text-stone-400 hover:text-red-500 transition-colors disabled:opacity-40"
                aria-label={`Remove ${kw.keyword}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd(input)}
          placeholder="New keyword"
          className="flex-1 text-xs border border-stone-200 bg-white px-3 py-1.5 outline-none focus:border-stone-400 placeholder:text-stone-300"
        />
        <button
          onClick={() => handleAdd(input)}
          disabled={busy || !input.trim()}
          className="px-3 py-1.5 text-xs border border-stone-300 text-stone-700 hover:border-stone-500 transition-colors disabled:opacity-40"
        >
          Add
        </button>
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={busy}
          className="px-5 py-2 text-xs bg-stone-900 text-stone-50 hover:bg-stone-700 transition-colors disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  )
}

const WAITLIST_QUERY = gql(/* GraphQL */ `
  query AdminWaitlist($first: Int = 50, $after: String) {
    internalAdminWaitlistEntries(first: $first, after: $after) {
      edges {
        node {
          id
          email
          keywords
          expandedKeywords
          createdAt
          matchedUser {
            id
            fullName
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

const IMPORT_WAITLIST_KEYWORDS_MUTATION = gql(/* GraphQL */ `
  mutation ImportWaitlistKeywords($input: InternalAdminImportWaitlistKeywordsInput!) {
    internalAdminImportWaitlistKeywords(input: $input) {
      user {
        id
        fullName
      }
    }
  }
`)

const UPDATE_WAITLIST_EXPANDED_KEYWORDS = gql(/* GraphQL */ `
  mutation UpdateWaitlistExpandedKeywords(
    $input: InternalAdminUpdateWaitlistExpandedKeywordsInput!
  ) {
    internalAdminUpdateWaitlistExpandedKeywords(input: $input) {
      waitlistEntry {
        id
        expandedKeywords
      }
    }
  }
`)

type WaitlistEntry = {
  id: string
  email: string
  keywords?: string | null
  expandedKeywords?: string | null
  createdAt: string
  matchedUser?: { id: string; fullName: string } | null
}

const WaitlistKeywordEditorDialog: React.FC<{
  entry: WaitlistEntry
  onClose: () => void
}> = ({ entry, onClose }) => {
  const [chips, setChips] = useState<string[]>(() =>
    entry.expandedKeywords
      ? entry.expandedKeywords
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : []
  )
  const [input, setInput] = useState("")

  const [updateKeywords, { loading: saving }] = useMutation(UPDATE_WAITLIST_EXPANDED_KEYWORDS, {
    refetchQueries: [WAITLIST_QUERY],
    onError: (err) => toast.error(err.message),
  })

  const handleAdd = () => {
    const trimmed = input.trim().toLowerCase()
    if (!trimmed || chips.includes(trimmed)) return
    setChips((prev) => [...prev, trimmed])
    setInput("")
  }

  const handleSave = async () => {
    await updateKeywords({
      variables: { input: { waitlistEntryId: entry.id, expandedKeywords: chips } },
      onCompleted: () => {
        toast.success("Saved")
        onClose()
      },
    })
  }

  return (
    <div className="space-y-5">
      {entry.keywords && (
        <div>
          <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-1">
            Signed up hunting for
          </p>
          <p className="text-xs text-stone-500">{entry.keywords}</p>
        </div>
      )}

      <div>
        <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-2">
          Expanded keywords
        </p>
        <div className="flex flex-wrap gap-2 min-h-[32px]">
          {chips.length === 0 && <p className="text-xs text-stone-400">No keywords yet.</p>}
          {chips.map((kw) => (
            <span
              key={kw}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-stone-100 text-stone-700 text-xs rounded-full"
            >
              {kw}
              <button
                onClick={() => setChips((prev) => prev.filter((k) => k !== kw))}
                disabled={saving}
                className="text-stone-400 hover:text-red-500 transition-colors disabled:opacity-40"
                aria-label={`Remove ${kw}`}
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="New keyword"
          className="flex-1 text-xs border border-stone-200 bg-white px-3 py-1.5 outline-none focus:border-stone-400 placeholder:text-stone-300"
        />
        <button
          onClick={handleAdd}
          disabled={saving || !input.trim()}
          className="px-3 py-1.5 text-xs border border-stone-300 text-stone-700 hover:border-stone-500 transition-colors disabled:opacity-40"
        >
          Add
        </button>
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 text-xs bg-stone-900 text-stone-50 hover:bg-stone-700 transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  )
}

const WaitlistTab: React.FC = () => {
  const { data, loading, error, fetchMore } = useQuery(WAITLIST_QUERY)
  const [importKeywords, { loading: importing }] = useMutation(IMPORT_WAITLIST_KEYWORDS_MUTATION)
  const [editingEntry, setEditingEntry] = useState<WaitlistEntry | null>(null)
  const entries = (data?.internalAdminWaitlistEntries.edges.map((e) => e.node) ?? []).filter(
    (e): e is NonNullable<typeof e> => e != null
  )
  const pageInfo = data?.internalAdminWaitlistEntries.pageInfo

  if (loading) return <ScreenLoading />
  if (error) return <GraphqlError error={error} />

  return (
    <div>
      <div className="rounded-md border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left">
              <th className="px-4 py-2 font-semibold text-stone-700">Email</th>
              <th className="px-4 py-2 font-semibold text-stone-700">Keywords</th>
              <th className="px-4 py-2 font-semibold text-stone-700">Signed up</th>
              <th className="px-4 py-2 font-semibold text-stone-700">Joined</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={entry.id} className="border-b last:border-0 hover:bg-stone-50">
                <td className="px-4 py-2 text-stone-900">{entry.email}</td>
                <td className="px-4 py-2 text-stone-500 max-w-[280px] truncate">
                  {entry.keywords ?? <span className="text-stone-300">—</span>}
                </td>
                <td className="px-4 py-2 text-stone-500">
                  {entry.matchedUser ? (
                    <span className="text-emerald-700 font-medium">
                      {entry.matchedUser.fullName}
                    </span>
                  ) : (
                    <span className="text-stone-300">No account</span>
                  )}
                </td>
                <td className="px-4 py-2 text-stone-400">
                  {new Date(entry.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingEntry(entry)}
                      className="text-xs text-stone-500 hover:text-stone-900 underline"
                    >
                      Edit keywords
                    </button>
                    {entry.matchedUser && entry.keywords && (
                      <button
                        disabled={importing}
                        onClick={() =>
                          importKeywords({
                            variables: { input: { waitlistEntryId: entry.id } },
                            onError: (err) => toast.error(err.message),
                            onCompleted: (d) =>
                              toast.success(
                                `Keywords imported to ${d.internalAdminImportWaitlistKeywords.user.fullName}`
                              ),
                          })
                        }
                        className="px-3 py-1 text-[11px] bg-stone-900 text-stone-50 hover:bg-stone-700 transition-colors disabled:opacity-50"
                      >
                        Import keywords
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-stone-400">
                  No waitlist entries.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {pageInfo?.hasNextPage && (
        <div className="mt-4">
          <Button
            variant="outline"
            onClick={() => fetchMore({ variables: { after: pageInfo.endCursor } })}
          >
            Load more
          </Button>
        </div>
      )}

      <Dialog open={editingEntry !== null} onOpenChange={(open) => !open && setEditingEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-stone-900">
              {editingEntry?.email ?? ""}
            </DialogTitle>
          </DialogHeader>
          {editingEntry && (
            <WaitlistKeywordEditorDialog
              entry={editingEntry}
              onClose={() => setEditingEntry(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const InternalAdminDashboardScreen = () => {
  const { data, fetchMore, error, loading } = useQuery(usersQuery)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [keywordUserId, setKeywordUserId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<"users" | "waitlist">("users")

  const users = data?.internalAdminUsers.edges.map((edge) => edge.node) ?? []
  const pageInfo = data?.internalAdminUsers.pageInfo
  const columns = buildColumns(setSelectedUserId, setKeywordUserId)

  const keywordUser = keywordUserId ? (users.find((u) => u?.id === keywordUserId) ?? null) : null

  return (
    <TablePageLayout>
      <div className="flex items-center gap-6 mb-4">
        <h1 className="text-2xl">Admin</h1>
        <div className="flex gap-2">
          {(["users", "waitlist"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm capitalize border transition-colors ${
                activeTab === tab
                  ? "bg-stone-900 text-stone-50 border-stone-900"
                  : "text-stone-600 border-stone-200 hover:border-stone-400"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      <hr className="my-4 border-0 border-b" />

      {activeTab === "waitlist" ? (
        <WaitlistTab />
      ) : !data && loading ? (
        <ScreenLoading />
      ) : error ? (
        <GraphqlError error={error} />
      ) : (
        <>
          <DataTable columns={columns} data={users} />
          {pageInfo && pageInfo.hasNextPage && (
            <>
              <hr className="my-4 border-0 border-b" />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="disabled:bg-gray-100"
                  onClick={() => {
                    fetchMore({
                      variables: {
                        after: pageInfo.endCursor,
                      },
                    })
                  }}
                  disabled={!pageInfo.hasNextPage}
                >
                  Load more
                </Button>
              </div>
            </>
          )}
        </>
      )}

      <Sheet
        open={selectedUserId !== null}
        onOpenChange={(open) => !open && setSelectedUserId(null)}
      >
        <SheetContent side="right" className="p-0 flex flex-col w-[380px]">
          <SheetTitle className="sr-only">User Search Settings</SheetTitle>
          {selectedUserId && (
            <UserSearchSettingsPanel
              userId={selectedUserId}
              onClose={() => setSelectedUserId(null)}
            />
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={keywordUser !== null} onOpenChange={(open) => !open && setKeywordUserId(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-stone-900">
              {keywordUser?.fullName ?? ""}
            </DialogTitle>
          </DialogHeader>
          {keywordUser && (
            <KeywordEditorDialog user={keywordUser} onClose={() => setKeywordUserId(null)} />
          )}
        </DialogContent>
      </Dialog>
    </TablePageLayout>
  )
}
